import merge from 'lodash.merge'
import type {
  ChatMessage,
  Choice,
  CompletionChunk,
  CompletionUserMessage,
  LogEntry,
  HydratedLogEntry,
  ToolCallMetadataMap,
  CompletionToolMessage,
} from '../types/session'
import {
  isAssistantIntentMessage,
  isAssistantTurnStartMessage,
  isChatMessage,
  isCommandMessage,
  isCompletionChunk,
  isCompletionToolMessage,
  isSessionIdleMessage,
  isToolOutputsMessage,
  isToolUserRequestedMessage,
} from '../types/session'
import {
  isAbortMessage,
  isExitPlanModeRequestedMessage,
  isExitPlanModeCompletedMessage,
  isRemoteSteerableChangedMessage,
} from '../types/session-events'
import {
  isInteractivePromptToolExecution,
  hasInteractivePromptInChunk,
  extractInteractivePromptsFromChunk,
  toToolUserRequestedMessage,
} from '../components/session-logs/prompt-log-utils'
import mergeWith from 'lodash.mergewith'
import type {CustomAgentGroupLogEntry} from './group-agent-logs'
import {groupCustomAgentLogs, getCustomAgentId} from './group-agent-logs'
import {parseCopilotAnnotationsToCodeInsights} from './parse-copilot-annotations-to-code-insights'
import {TOOL_NAMES} from '../constants/tool-names'
import {stripPlanTags} from './plan-tags'

/**
 * Checks if a log entry is an assistant stop message (shape-based).
 * Returns true for any main-agent CompletionChunk with finish_reason="stop",
 * content, and no tool_calls.
 */
export function isAssistantStopEntry(logEntry: HydratedLogEntry): boolean {
  if (!isCompletionChunk(logEntry)) return false

  // Subagent stop messages should stay in logs, not be filtered
  if (getCustomAgentId(logEntry)) return false

  const chunk = logEntry as CompletionChunk
  const choice = chunk.choices[0]
  return !!(
    choice?.finish_reason === 'stop' &&
    choice.delta.role === 'assistant' &&
    choice.delta.content &&
    !hasToolCalls(choice)
  )
}

/**
 * Identifies trailing final stop entries by walking backward from the end of
 * the preprocessed entries list. Only consecutive assistant stop entries at the
 * tail (after all tool calls and other visible content) are considered "final".
 * Non-rendered entries (e.g. empty stop chunks) are skipped during the walk.
 */
function getTrailingFinalStopIds(entries: HydratedLogEntry[]): Set<string> {
  const ids = new Set<string>()

  for (let i = entries.length - 1; i >= 0; i--) {
    const entry = entries[i]
    if (!entry) break

    // Skip non-CompletionChunk entries (e.g. lifecycle events like session.idle)
    if (!isCompletionChunk(entry)) continue

    if (isAssistantStopEntry(entry)) {
      const chunk = entry as CompletionChunk
      ids.add(chunk.id)
      continue
    }

    // Skip non-rendered entries: empty stop chunks with no content and no tool calls
    if (isCompletionChunk(entry) && !getCustomAgentId(entry)) {
      const chunk = entry as CompletionChunk
      const choice = chunk.choices[0]
      if (choice?.finish_reason === 'stop' && !choice.delta.content && !hasToolCalls(choice)) {
        continue
      }
    }

    // Hit a visible non-stop entry — stop collecting
    break
  }

  return ids
}

type LogProcessingOptions = {
  preserveAssistantNarration?: boolean
  groupCustomAgentSessions?: boolean
  filterFinalStopMessage?: boolean
}

/**
 * Copies assistant narration to `assistant_content` when a choice has both `content` and `tool_calls`.
 * Prevents narration from being overwritten when tool results are merged during deduplication.
 */
function preserveAssistantContent(logEntry: CompletionChunk): CompletionChunk {
  let hasUpdates = false
  const choices = logEntry.choices.map(choice => {
    const shouldPreserveAssistantContent =
      choice.delta.role === 'assistant' &&
      !!choice.delta.content &&
      !choice.delta.assistant_content &&
      hasToolCalls(choice)
    if (!shouldPreserveAssistantContent) {
      return choice
    }
    hasUpdates = true
    return {
      ...choice,
      delta: {
        ...choice.delta,
        assistant_content: choice.delta.content,
      },
    }
  })

  if (!hasUpdates) {
    return logEntry
  }

  return {
    ...logEntry,
    choices,
  }
}

function maybePreserveAssistantContent(logEntry: CompletionChunk, shouldPreserve: boolean): CompletionChunk {
  if (!shouldPreserve) {
    return logEntry
  }
  return preserveAssistantContent(logEntry)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function customChoiceMerger(firstValue: any, secondValue: any): any {
  // If we're combining choices arrays, combine tool calls with the same ID
  if (
    Array.isArray(firstValue) &&
    Array.isArray(secondValue) &&
    firstValue[0]?.hasOwnProperty('delta') &&
    secondValue[0]?.hasOwnProperty('delta')
  ) {
    const finalChoices = new Array<Choice>()
    const idToChoiceWithTool = new Map<string, Choice>()
    // Combine choices with the same tool ID
    for (const choice of firstValue.concat(secondValue)) {
      // Explicit assumption there will only be one tool call in a choice
      const tool = choice.delta.tool_calls?.[0]
      // Choices without tool calls are always unique
      if (!tool || !tool.id) {
        finalChoices.push(choice)
        continue
      }
      let newChoice = choice
      const existingChoice = idToChoiceWithTool.get(tool.id)
      if (existingChoice) {
        newChoice = merge({}, existingChoice, choice)
      }
      idToChoiceWithTool.set(tool.id, newChoice)
    }
    return finalChoices.concat(Array.from(idToChoiceWithTool.values()))
  }
  // Otherwise, use the default merge behavior
  return undefined
}

function extractToolCallMetadataFromChunk(chunk: CompletionChunk): ToolCallMetadataMap | undefined {
  const toolCallMetadata: ToolCallMetadataMap = {}

  // Process each choice and associate its annotations with its specific tool call
  for (const choice of chunk.choices) {
    const copilotAnnotations = choice.delta?.copilot_annotations
    if (!copilotAnnotations) {
      continue
    }

    const codeInsights = parseCopilotAnnotationsToCodeInsights(copilotAnnotations)
    if (!codeInsights) {
      continue
    }

    // Associate insights with the specific tool call in this choice
    // In practice there's only one tool call per choice
    const toolCalls = choice.delta?.tool_calls ?? []
    for (const toolCall of toolCalls) {
      if (toolCall.id) {
        const existingInsights = toolCallMetadata[toolCall.id]?.['code-insights']
        if (existingInsights) {
          // Merge with existing insights for this tool call
          toolCallMetadata[toolCall.id] = {
            'code-insights': {
              publicCodeReferences: [...existingInsights.publicCodeReferences, ...codeInsights.publicCodeReferences],
            },
          }
        } else {
          toolCallMetadata[toolCall.id] = {'code-insights': codeInsights}
        }
      }
    }
  }

  return Object.keys(toolCallMetadata).length > 0 ? toolCallMetadata : undefined
}

/**
 * Merges two tool-call metadata maps, concatenating code-insights arrays for matching tool_call_ids.
 * Deduplicates publicCodeReferences by annotationId to avoid duplicates when the same tool
 * appears in multiple chunks.
 */
function mergeToolCallMetadata(
  existing: ToolCallMetadataMap | undefined,
  incoming: ToolCallMetadataMap | undefined,
): ToolCallMetadataMap | undefined {
  if (!incoming) {
    return existing
  }
  if (!existing) {
    return incoming
  }

  const merged: ToolCallMetadataMap = {...existing}

  for (const [toolCallId, incomingBag] of Object.entries(incoming)) {
    const existingBag = merged[toolCallId]
    if (!existingBag) {
      merged[toolCallId] = incomingBag
      continue
    }

    // Merge code-insights if both have it
    const existingInsights = existingBag['code-insights']
    const incomingInsights = incomingBag['code-insights']

    if (existingInsights && incomingInsights) {
      // Deduplicate publicCodeReferences by annotationId
      const seenIds = new Set(existingInsights.publicCodeReferences.map(ref => ref.annotationId))
      const newRefs = incomingInsights.publicCodeReferences.filter(ref => !seenIds.has(ref.annotationId))

      merged[toolCallId] = {
        ...existingBag,
        ...incomingBag,
        'code-insights': {
          publicCodeReferences: [...existingInsights.publicCodeReferences, ...newRefs],
        },
      }
    } else {
      merged[toolCallId] = {...existingBag, ...incomingBag}
    }
  }

  return merged
}

/**
 * Extracts content from a tool message, handling both string and array formats.
 */
function extractToolResultContent(toolMessage: CompletionToolMessage): string {
  if (typeof toolMessage.content === 'string') {
    return toolMessage.content
  }
  return toolMessage.content.map(c => c.text).join('\n')
}

/**
 * Merges tool result content into the matching choice within a completion chunk.
 * Returns a new chunk with the result content added to the matching tool call's delta.
 */
function mergeToolResultIntoChunk(chunk: CompletionChunk, toolMessage: CompletionToolMessage): CompletionChunk {
  const resultContent = extractToolResultContent(toolMessage)
  const updatedChoices = chunk.choices.map(choice => {
    const toolCalls = choice.delta.tool_calls ?? []
    const hasMatchingToolCall = toolCalls.some(tc => tc.id === toolMessage.tool_call_id)
    if (hasMatchingToolCall) {
      return {
        ...choice,
        delta: {
          ...choice.delta,
          content: resultContent,
        },
      }
    }
    return choice
  })
  return {...chunk, choices: updatedChoices}
}

/**
 * Merges content from two assistant chat messages with choices.
 * Concatenates the content from the first choice of each message.
 */
function mergeChatMessageContent(existing: ChatMessage, incoming: ChatMessage): ChatMessage {
  const existingContent = existing.choices?.[0]?.delta?.content ?? ''
  const newContent = incoming.choices?.[0]?.delta?.content ?? ''
  const existingChoice = existing.choices?.[0]
  return {
    ...incoming,
    choices: existingChoice
      ? [
          {
            ...existingChoice,
            delta: {
              ...existingChoice.delta,
              content: existingContent + newContent,
            },
          },
        ]
      : incoming.choices,
  }
}

/**
 * Extracts intent text from report_intent tool calls in a CompletionChunk.
 * This is a fallback for when assistant.intent events aren't emitted
 * (e.g., the CLI chat-completion-client path doesn't generate them).
 */
function extractIntentFromToolCalls(chunk: CompletionChunk): string | null {
  for (const choice of chunk.choices) {
    for (const toolCall of choice.delta.tool_calls ?? []) {
      if (toolCall.function?.name === TOOL_NAMES.REPORT_INTENT && toolCall.function.arguments) {
        try {
          const parsed = JSON.parse(toolCall.function.arguments) as {intent?: string}
          if (parsed.intent) {
            return parsed.intent
          }
        } catch {
          // Arguments may be incomplete/malformed
        }
      }
    }
  }
  return null
}

export function dedupeCompletionChunks(
  completionChunks: CompletionChunk[] | undefined,
  options: LogProcessingOptions = {},
): CompletionChunk[] {
  if (!completionChunks) {
    return []
  }
  const shouldPreserve = options.preserveAssistantNarration ?? false
  const idToLogEntry = new Map<string, CompletionChunk>()

  for (const logEntry of completionChunks) {
    const logEntryWithAssistantContent = maybePreserveAssistantContent(logEntry, shouldPreserve)
    let newEntry = logEntryWithAssistantContent
    const existingEntry = idToLogEntry.get(logEntry.id)
    // If we already know about a logEntry with that ID, combine the log entries
    if (existingEntry) {
      newEntry = mergeWith({}, existingEntry, logEntryWithAssistantContent, customChoiceMerger)
    }
    idToLogEntry.set(logEntry.id, newEntry)
  }

  return [...idToLogEntry.values()]
}

export type PreprocessedLogResult = {
  entries: CustomAgentGroupLogEntry[]
  currentIntent: string | null
  isWorking: boolean
  abortedToolCallIds: Set<string>
}

export function preprocessLogEntries(
  logEntries: LogEntry[] | undefined,
  options: LogProcessingOptions = {},
): PreprocessedLogResult {
  if (!logEntries) {
    return {entries: [], currentIntent: null, isWorking: false, abortedToolCallIds: new Set()}
  }
  const shouldPreserve = options.preserveAssistantNarration ?? false
  const idToLogEntry = new Map<string, HydratedLogEntry>()
  // Track tool calls by their toolCallId for merging with CompletionToolMessage results
  // This allows us to merge tool.execution_complete results back into the tool call chunk
  const toolCallIdToChunkId = new Map<string, string>()
  let previousCommandId = null
  let previousLogEntries: HydratedLogEntry[] = []
  let currentIntent: string | null = null
  let isWorking = false

  const resolvedToolCallIds = new Set<string>()
  const abortedToolCallIds = new Set<string>()
  const pendingInteractiveToolCallIds: string[] = []
  // Map exit_plan_mode requestId → toolCallId for correlating completed events
  const planRequestIdToToolCallId = new Map<string, string>()
  // Map exit_plan_mode requestId → resolution data from completed events
  const planResolutions = new Map<string, {approved: boolean; selectedAction?: string; feedback?: string}>()
  for (const entry of logEntries) {
    if (isToolOutputsMessage(entry)) {
      resolvedToolCallIds.add(entry.toolCallId)
    } else if (isCompletionToolMessage(entry)) {
      resolvedToolCallIds.add(entry.tool_call_id)
    } else if (isToolUserRequestedMessage(entry)) {
      pendingInteractiveToolCallIds.push(entry.toolCallId)
    } else if (isExitPlanModeRequestedMessage(entry)) {
      pendingInteractiveToolCallIds.push(entry.data.toolCallId)
      planRequestIdToToolCallId.set(entry.data.requestId, entry.data.toolCallId)
    } else if (isExitPlanModeCompletedMessage(entry)) {
      const toolCallId = planRequestIdToToolCallId.get(entry.data.requestId)
      if (toolCallId) {
        resolvedToolCallIds.add(toolCallId)
      }
      planResolutions.set(entry.data.requestId, {
        approved: entry.data.approved ?? false,
        selectedAction: entry.data.selectedAction,
        feedback: entry.data.feedback,
      })
    } else if (hasInteractivePromptInChunk(entry)) {
      // Interactive prompts can also arrive as CompletionChunks (e.g. exit_plan_mode)
      for (const prompt of extractInteractivePromptsFromChunk(entry)) {
        pendingInteractiveToolCallIds.push(prompt.toolCallId)
      }
    } else if (isAbortMessage(entry)) {
      // Abort resolves all pending interactive prompts that precede it
      for (const id of pendingInteractiveToolCallIds) {
        resolvedToolCallIds.add(id)
        abortedToolCallIds.add(id)
      }
      pendingInteractiveToolCallIds.length = 0
    }
  }

  if (logEntries !== undefined && isChatMessage(logEntries[0])) {
    // We already show the first message from a session chat sequence in
    // from the session content, so exclude it
    logEntries.shift()
  }

  for (const logEntry of logEntries) {
    // Subagent events should not affect top-level working state or intent
    const isSubagentEvent = !!getCustomAgentId(logEntry)

    // Handle session lifecycle events for working state tracking
    if (isSessionIdleMessage(logEntry) && !isSubagentEvent) {
      isWorking = false
      currentIntent = null
      continue
    }

    if (isAbortMessage(logEntry) && !isSubagentEvent) {
      isWorking = false
      currentIntent = null
      continue
    }

    // Show inline notification when remote steering is enabled
    if (isRemoteSteerableChangedMessage(logEntry) && logEntry.data.remoteSteerable) {
      if (!isSubagentEvent) {
        isWorking = false
      }
      idToLogEntry.set(logEntry.id, logEntry)
      continue
    }

    if (isAssistantTurnStartMessage(logEntry) && !isSubagentEvent) {
      isWorking = true
      // Only clear intent on the first turn
      if (logEntry.data.turnId === '0') {
        currentIntent = null
      }

      continue
    }

    if (isAssistantIntentMessage(logEntry) && !isSubagentEvent) {
      currentIntent = logEntry.data.intent
      continue
    }

    if (isToolUserRequestedMessage(logEntry)) {
      if (!isSubagentEvent && !resolvedToolCallIds.has(logEntry.toolCallId)) {
        isWorking = false
      }

      idToLogEntry.set(logEntry.toolCallId, logEntry)
      previousCommandId = null
      previousLogEntries = []
      continue
    }

    // Handle exit_plan_mode.requested — convert to ToolUserRequestedMessage with richer data
    if (isExitPlanModeRequestedMessage(logEntry)) {
      const resolution = planResolutions.get(logEntry.data.requestId)
      const message = toToolUserRequestedMessage(
        {
          toolCallId: logEntry.data.toolCallId,
          toolName: 'exit_plan_mode',
          arguments: {
            summary: logEntry.data.summary,
            planContent: logEntry.data.planContent,
            actions: logEntry.data.actions,
            recommendedAction: logEntry.data.recommendedAction,
            requestId: logEntry.data.requestId,
            resolution,
          },
        },
        logEntry.timestamp,
      )
      if (!isSubagentEvent && !resolvedToolCallIds.has(logEntry.data.toolCallId)) {
        isWorking = false
      }
      idToLogEntry.set(message.toolCallId, message)
      previousCommandId = null
      previousLogEntries = []
      continue
    }

    // Convert tool.execution_start for interactive prompts to ToolUserRequestedMessage
    if (isInteractivePromptToolExecution(logEntry)) {
      const message = toToolUserRequestedMessage({
        toolCallId: logEntry.data.toolCallId,
        toolName: logEntry.data.toolName,
        arguments: logEntry.data.arguments,
      })
      idToLogEntry.set(message.toolCallId, message)
      previousCommandId = null
      previousLogEntries = []
      continue
    }

    // Handle chat messages first - they have threadID and may or may not have choices so non-chat messages will never
    // match isChatMessage but chat messages might match isCompletionChunk
    if (isChatMessage(logEntry)) {
      const existingEntry = idToLogEntry.get(logEntry.id)
      if (existingEntry && isChatMessage(existingEntry as LogEntry)) {
        // For assistant messages with choices, concatenate the content
        if (logEntry.role === 'assistant' && logEntry.choices && (existingEntry as ChatMessage).choices) {
          const mergedEntry = mergeChatMessageContent(existingEntry as ChatMessage, logEntry)
          idToLogEntry.set(logEntry.id, mergedEntry)
        } else {
          // For user messages or messages without choices, just replace
          idToLogEntry.set(logEntry.id, logEntry)
        }
      } else {
        idToLogEntry.set(logEntry.id, logEntry)
      }
    } else if (isCompletionToolMessage(logEntry)) {
      // Handle tool result messages (role: "tool") from CLI tool.execution_complete events
      // These contain the result content that should be merged back into the corresponding tool call chunk
      // NOTE: This assumes tool results arrive AFTER their tool call chunk.
      const toolMessage = logEntry
      const chunkId = toolCallIdToChunkId.get(toolMessage.tool_call_id)
      if (chunkId) {
        const existingChunk = idToLogEntry.get(chunkId) as CompletionChunk | undefined
        if (existingChunk && isCompletionChunk(existingChunk)) {
          idToLogEntry.set(chunkId, mergeToolResultIntoChunk(existingChunk, toolMessage))
        }
      }
      previousCommandId = null
      previousLogEntries = []
    } else if (isCompletionChunk(logEntry)) {
      const logEntryWithAssistantContent = maybePreserveAssistantContent(logEntry, shouldPreserve)

      // Extract intent from report_intent tool calls as a fallback
      const extractedIntent = extractIntentFromToolCalls(logEntryWithAssistantContent)
      if (extractedIntent !== null) {
        currentIntent = extractedIntent
      }

      // Extract tool-call-scoped metadata from this chunk
      const incomingMetadata = extractToolCallMetadataFromChunk(logEntryWithAssistantContent)

      // Track tool call IDs for merging with later CompletionToolMessage results
      for (const choice of logEntryWithAssistantContent.choices) {
        for (const toolCall of choice.delta.tool_calls ?? []) {
          if (toolCall.id) {
            toolCallIdToChunkId.set(toolCall.id, logEntry.id)
          }
        }
      }

      let newEntry: HydratedLogEntry = logEntryWithAssistantContent
      const existingEntry = idToLogEntry.get(logEntry.id)
      // If we already know about a logEntry with that ID, combine the log entries
      if (existingEntry) {
        const mergedBase = mergeWith({}, existingEntry, logEntryWithAssistantContent, customChoiceMerger)

        // Merge tool_call_metadata separately to handle deduplication
        const existingMetadata = 'tool_call_metadata' in existingEntry ? existingEntry.tool_call_metadata : undefined
        const mergedMetadata = mergeToolCallMetadata(existingMetadata, incomingMetadata)

        newEntry = mergedMetadata ? {...mergedBase, tool_call_metadata: mergedMetadata} : mergedBase
      } else if (incomingMetadata) {
        newEntry = {...logEntryWithAssistantContent, tool_call_metadata: incomingMetadata}
      }

      idToLogEntry.set(logEntry.id, newEntry)

      // Extract interactive prompts AFTER the chunk is inserted so the assistant
      // text appears before the prompt UI in the timeline (Map insertion order).
      if (hasInteractivePromptInChunk(logEntryWithAssistantContent)) {
        const prompts = extractInteractivePromptsFromChunk(logEntryWithAssistantContent)
        const hasUnresolvedPrompt = prompts.some(p => !resolvedToolCallIds.has(p.toolCallId))
        for (const prompt of prompts) {
          const message = toToolUserRequestedMessage(prompt)
          idToLogEntry.set(message.toolCallId, message)
        }

        if (hasUnresolvedPrompt && !isSubagentEvent) {
          isWorking = false
        }
      }

      previousCommandId = null
      previousLogEntries = []
    } else if (isCommandMessage(logEntry)) {
      // If the previous entry was a command message, combine the two into a group
      let commandId = null
      let entries: CompletionUserMessage[] = []
      if (previousCommandId && previousLogEntries.length > 0) {
        commandId = previousCommandId
        entries = [...(previousLogEntries as CompletionUserMessage[]), logEntry]
      } else {
        commandId = logEntry.source
        entries = [logEntry]
      }
      idToLogEntry.set(commandId, entries)
      previousCommandId = commandId
      previousLogEntries = entries
    }
  }

  const preprocessed = [...idToLogEntry.values()]

  // Filter out only the trailing final stop messages when they're being rendered outside the logs.
  // Intermediate assistant stop entries (thinking messages between tool calls) must stay inline.
  const trailingFinalStopIds = options.filterFinalStopMessage
    ? getTrailingFinalStopIds(preprocessed)
    : new Set<string>()
  const filteredPreprocessed = options.filterFinalStopMessage
    ? preprocessed.filter(entry => {
        if (!isAssistantStopEntry(entry)) {
          return true
        }
        const chunk = entry as CompletionChunk
        return !trailingFinalStopIds.has(chunk.id)
      })
    : preprocessed

  // Strip <plan> tag markers from any remaining stop entries so they don't
  // render as raw text in the session logs.  We replace entries immutably to
  // avoid mutating objects that may be shared with react-query cache data.
  for (let i = 0; i < filteredPreprocessed.length; i++) {
    const entry = filteredPreprocessed[i]
    if (!entry || !isAssistantStopEntry(entry)) continue
    const chunk = entry as CompletionChunk
    const choice = chunk.choices[0]
    if (choice?.delta.content) {
      filteredPreprocessed[i] = {
        ...chunk,
        choices: [{...choice, delta: {...choice.delta, content: stripPlanTags(choice.delta.content)}}],
      }
    }
  }

  if (options.groupCustomAgentSessions) {
    return {entries: groupCustomAgentLogs(filteredPreprocessed), currentIntent, isWorking, abortedToolCallIds}
  }

  return {entries: filteredPreprocessed, currentIntent, isWorking, abortedToolCallIds}
}

function hasToolCalls(choice: Choice): boolean {
  return !!(choice.delta.tool_calls && choice.delta.tool_calls.length > 0)
}
